import { useEffect, useState } from 'react';
import { getCatalog, getStoredSessionToken, listMyReconstructions } from '../api/catalog';
import { CatalogCard } from '../components/CatalogCard';
import { CreateReconstructionModal } from '../components/CreateReconstructionModal';
import { Header } from '../components/Header';
import { ReconstructionShareCard } from '../components/ReconstructionShareCard';
import type { CatalogItemSummary, ReconstructionSummary } from '../types';

export function HomePage() {
  const [items, setItems] = useState<CatalogItemSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createdReconstruction, setCreatedReconstruction] =
    useState<ReconstructionSummary | null>(null);
  const [reconstructions, setReconstructions] = useState<ReconstructionSummary[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getCatalog();
        if (!cancelled) setItems(data);
      } catch (err) {
        console.error('Failed to load catalog:', err);
        if (!cancelled) setError('לא הצלחנו לטעון את הקטלוג. נסו שוב.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the current user's reconstructions if they already have a session.
  useEffect(() => {
    let cancelled = false;
    const token = getStoredSessionToken();
    if (!token) return;
    listMyReconstructions(token)
      .then((data) => {
        if (!cancelled) setReconstructions(data);
      })
      .catch((err) => console.error('Failed to load reconstructions:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  function handleCreated(reconstruction: ReconstructionSummary) {
    setReconstructions((prev) => {
      const rest = prev.filter((r) => r.id !== reconstruction.id);
      return [reconstruction, ...rest];
    });
    setShowCreate(false);
    setCreatedReconstruction(reconstruction);
  }

  return (
    <div data-testid="catalog-home" className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-slate-100">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <section className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">קטלוג מבחנים ציבורי</p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
              בחרו את חוויית התרגול הבאה שלכם
            </h1>
            <p className="mt-4 text-lg leading-8 text-slate-600">
              עיינו במוצרי ההכנה של Clipiti ופתחו את חוויית המבחן שמתאימה למטרות שלכם.
            </p>
          </div>
          <button
            type="button"
            data-testid="create-reconstruction-button"
            onClick={() => setShowCreate(true)}
            className="btn-primary whitespace-nowrap"
          >
            + יצירת שחזור מבחן
          </button>
        </section>

        {reconstructions.length > 0 && (
          <section data-testid="my-reconstructions" className="mb-12">
            <h2 className="mb-4 text-2xl font-bold text-slate-950">השחזורים שלי</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {reconstructions.map((r) => (
                <ReconstructionShareCard key={r.id} reconstruction={r} />
              ))}
            </div>
          </section>
        )}

        {isLoading && (
          <div className="card p-8 text-center text-slate-600">טוען קטלוג…</div>
        )}

        {error && !isLoading && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
        )}

        {!isLoading && !error && items.length === 0 && (
          <div className="card p-10 text-center">
            <p className="text-lg font-semibold text-slate-900">עדיין אין מבחנים זמינים.</p>
            <p className="mt-2 text-slate-600">חזרו לבדוק בקרוב.</p>
          </div>
        )}

        {!isLoading && !error && items.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <CatalogCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </main>

      {showCreate && (
        <CreateReconstructionModal
          catalogItems={items}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
      {createdReconstruction && (
        <div
          data-testid="reconstruction-share-modal"
          className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/40 p-4"
          onClick={() => setCreatedReconstruction(null)}
        >
          <div
            className="w-full max-w-lg"
            dir="rtl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                data-testid="reconstruction-share-close"
                onClick={() => setCreatedReconstruction(null)}
                className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow"
              >
                סגירה
              </button>
            </div>
            <ReconstructionShareCard reconstruction={createdReconstruction} />
          </div>
        </div>
      )}
    </div>
  );
}
